import { useRef, useState } from 'react';
import PeriodPicker from './PeriodPicker';
import { PRESETS_MONTHLY } from '../utils/period';
import MultiSelectFilter from './MultiSelectFilter';
import MoreFilters from './MoreFilters';
import FilterButton from './FilterButton';
import FilterSheet from './FilterSheet';
import { SortOptions } from './MobileSort';
import { useIsMobile } from '../utils/useIsMobile';
import { useStickyFilterOffset } from '../utils/useStickyFilterOffset';
import Icon from './Icon';
import { LIFECYCLE_FILTER_OPTIONS } from '../utils/paymentLifecycle';
import { formatIsoDayShort } from '../utils/dateFormat';

// 'YYYY-MM-DD' -> 'dd/mm/yy'. Empty input returns empty string.
const fmtDate = (s) => (s ? formatIsoDayShort(s) : '');

// Filter bar scoped to /suppliers. Follows FILTER_AND_DATE_RULES.md (same
// spec as /documents): categorical filters cluster on the start side, date
// picker anchored to the far end via .docs-filter-datewrap (margin-inline-
// start: auto), active-filter chips render below (date excluded), and
// "Clear all" sits last in the chip row.
//
// Visible categorical filters: search, payment terms, due-date. There is NO
// payment-status filter: /suppliers is the לתשלום tab and is permanently
// scoped to open items, with /suppliers/paid as the שולם tab. The view tabs
// (PaymentViewTabs) replaced that filter — the two views differ in their
// columns, which a filter cannot change.
//
// ≤720px the bar collapses to search + a single "סינון" trigger; every other
// filter moves into a FilterSheet (bottom sheet). Filters commit instantly
// in both layouts; the sheet footer shows a live result count.
export default function SuppliersFilterBar({
  nameFilter,      setNameFilter,
  termsFilter,     setTermsFilter,
  termsOptions,
  dueDateFilter,   setDueDateFilter,
  // Multi-select on the supplier-month payment lifecycle (utils/paymentLifecycle.js).
  lifecycleFilter, setLifecycleFilter,
  dateFrom,        dateTo,
  setDateRange,
  onClearAll,
  resultCount,
  // Sort state, data-driven rather than a rendered node — on mobile it's
  // folded into the same sheet as the other filters (one trigger, "סינון
  // ומיון", instead of a separate sort pill + sort sheet); on desktop this
  // page has no sort control at all (the table's own column headers sort),
  // so these three are simply unused there.
  sortOptions,
  sort,
  onSortChange,
}) {
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);

  const hasChips = Boolean(
    nameFilter ||
    (termsFilter && termsFilter.length) ||
    dueDateFilter ||
    (lifecycleFilter && lifecycleFilter.length)
  );
  // Count of filters that live inside "More" and are currently active —
  // shown as the count badge on the More trigger per spec §3.
  const moreActiveCount =
    ((termsFilter     && termsFilter.length     > 0) ? 1 : 0) +
    ((lifecycleFilter && lifecycleFilter.length > 0) ? 1 : 0);
  // Badge on the mobile "סינון" trigger — everything that lives in the sheet
  // except the date range (which, like the chips row, is treated as scope
  // rather than a filter — matching desktop chip semantics).
  const sheetActiveCount = moreActiveCount + (dueDateFilter ? 1 : 0);

  // Native <input type="date"> only opens the picker when the user clicks
  // the tiny calendar glyph — clicking the dd/mm/yyyy placeholder text just
  // focuses the field without opening anything. `showPicker()` is the
  // standard way to open it programmatically; wire to the whole pill so any
  // click (including on the input or the microlabel) opens it.
  const dueDateInputRef = useRef(null);
  const openDueDatePicker = () => {
    try { dueDateInputRef.current?.showPicker?.(); } catch {}
  };
  const wrapRef = useRef(null);
  useStickyFilterOffset(wrapRef);

  // Search — text input wears the same "control" chrome as the selects so
  // the bar reads as one row. Shared by both layouts (mobile keeps it in the
  // bar; only the rest moves into the sheet).
  const searchPill = (
    <label className={`docs-filter-control docs-filter-searchpill${nameFilter ? ' is-active' : ''}`}>
      <Icon name="search" size={14} className="docs-filter-microlabel" />
      <input
        className="docs-filter-select"
        value={nameFilter}
        onChange={(e) => setNameFilter(e.target.value)}
        placeholder="חפש לפי שם ספק"
        style={{ minWidth: isMobile ? 0 : 160 }}
      />
    </label>
  );

  // Due-date — semantics: "show suppliers due EXACTLY on this date".
  // The whole pill opens the picker (not just the calendar glyph).
  const dueDatePill = (
    <label
      className={`docs-filter-control${dueDateFilter ? ' is-active' : ''}`}
      style={{ cursor: 'pointer' }}
      onClick={openDueDatePicker}
    >
      <span className="docs-filter-microlabel">לתשלום ב</span>
      <input
        ref={dueDateInputRef}
        type="date"
        className="docs-filter-select date-icon-only"
        value={dueDateFilter}
        onChange={(e) => setDueDateFilter(e.target.value)}
      />
      {dueDateFilter && (
        <span style={{ fontSize: 13, color: 'var(--selected-soft-ink)', whiteSpace: 'nowrap' }}>
          {fmtDate(dueDateFilter)}
        </span>
      )}
    </label>
  );

  // One date control for both months and arbitrary day ranges. PRESETS_MONTHLY
  // because the payment approval is filed per calendar month — only a range
  // that names one month can be approved (see server/lib/monthKey.js).
  //
  // Disabled while the due-date filter is active, since the spec calls for
  // due-date scope to override the bar's range. That's a real prop now rather
  // than the opacity/pointer-events wrapper this used to carry — the wrapper
  // also held `marginInlineStart` layout logic that the mobile pill must not
  // inherit.
  const dateRange = (variant) => (
    <PeriodPicker
      dateFrom={dateFrom}
      dateTo={dateTo}
      onChange={(f, t) => setDateRange(f, t)}
      presets={PRESETS_MONTHLY}
      variant={variant}
      disabled={!!dueDateFilter}
      disabledReason='טווח התאריכים מתעלם בעת סינון לפי "לתשלום ב"'
    />
  );

  const statusFilters = (inline) => (
    <>
      <MultiSelectFilter
        inline={inline}
        label="תנאי תשלום"
        values={termsFilter}
        options={termsOptions}
        onChange={setTermsFilter}
      />
      <MultiSelectFilter
        inline={inline}
        label="מצב תשלום"
        values={lifecycleFilter}
        options={LIFECYCLE_FILTER_OPTIONS}
        onChange={setLifecycleFilter}
      />
    </>
  );

  const renderChips = () => (
    <div className="docs-filter-chips">
      {nameFilter && (
        <span className="docs-filter-chip">
          חיפוש: {nameFilter}
          <button
            type="button"
            aria-label="נקה חיפוש"
            onClick={() => setNameFilter('')}
            className="docs-filter-chip-x"
          ><Icon name="close" size={11} strokeWidth={2} /></button>
        </span>
      )}
      {termsFilter && termsFilter.length > 0 && (
        <span className="docs-filter-chip">
          {/* Truncate to 3 values then "+N" per spec §4. */}
          תנאי תשלום: {termsFilter.slice(0, 3).join(', ')}
          {termsFilter.length > 3 && ` +${termsFilter.length - 3}`}
          <button
            type="button"
            aria-label="נקה תנאי תשלום"
            onClick={() => setTermsFilter([])}
            className="docs-filter-chip-x"
          ><Icon name="close" size={11} strokeWidth={2} /></button>
        </span>
      )}
      {dueDateFilter && (
        <span className="docs-filter-chip">
          לתשלום ב: {fmtDate(dueDateFilter)}
          <button
            type="button"
            aria-label="נקה תאריך לתשלום"
            onClick={() => setDueDateFilter('')}
            className="docs-filter-chip-x"
          ><Icon name="close" size={11} strokeWidth={2} /></button>
        </span>
      )}
      {dueDateFilter && (
        <span className="docs-filter-chip" style={{ background: 'var(--tint-strong)', color: 'var(--ink-muted)' }}>
          טווח תאריכים מושבת בעת סינון לפי לתשלום
        </span>
      )}
      {lifecycleFilter && lifecycleFilter.length > 0 && (
        <span className="docs-filter-chip">
          מצב תשלום: {lifecycleFilter.slice(0, 3).join(', ')}
          {lifecycleFilter.length > 3 && ` +${lifecycleFilter.length - 3}`}
          <button
            type="button"
            aria-label="נקה מצב תשלום"
            onClick={() => setLifecycleFilter([])}
            className="docs-filter-chip-x"
          ><Icon name="close" size={11} strokeWidth={2} /></button>
        </span>
      )}
      <button type="button" className="docs-filter-clearall" onClick={onClearAll}>
        נקה הכל
      </button>
    </div>
  );

  if (isMobile) {
    // Sort counts toward the trigger's badge too — it's one merged sheet now,
    // so "something in here is non-default" should cover sort the same way
    // it already covers the filters.
    const hasSort = !!(sort && sort.field);
    const combinedActiveCount = sheetActiveCount + (hasSort ? 1 : 0);
    return (
      <>
      <div className="period-bar" ref={wrapRef}>{dateRange('bar')}</div>
      <div className="docs-filter-wrap">
        <div className="docs-filter-bar docs-filter-bar--mobile">
          {searchPill}
          <FilterButton
            label="סינון ומיון"
            activeCount={combinedActiveCount}
            expanded={sheetOpen}
            onClick={() => setSheetOpen(true)}
          />
        </div>
        {hasChips && renderChips()}

        <FilterSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title="סינון ומיון"
          resultCount={resultCount}
          onClearAll={onClearAll}
        >
          {sortOptions && sortOptions.length > 0 && (
            <>
              <div className="filter-sheet-section-label">מיון</div>
              <SortOptions options={sortOptions} sort={sort} onChange={onSortChange} />
            </>
          )}
          {dueDatePill}
          {statusFilters(true)}
        </FilterSheet>
      </div>
      </>
    );
  }

  return (
    <div className="docs-filter-wrap" ref={wrapRef}>
      <div className="docs-filter-bar">
        {searchPill}
        {dueDatePill}

        {/* "More" overflow — the terms and lifecycle filters live inside per
            spec §3 (less-used than the first-class filters above). */}
        <MoreFilters activeCount={moreActiveCount}>
          {statusFilters(false)}
        </MoreFilters>

        {dateRange('pill')}
      </div>

      {hasChips && renderChips()}
    </div>
  );
}
